import React, { useState, useEffect } from "react";
import { RotateCcw } from "lucide-react";
import { formatCurrency, formatDate, employeeNameFromPaycheque } from "../../utils/paychequeStatus";

const BORDER = "#E5E7EB";
const TEXT_PRIMARY = "#111827";
const TEXT_SECONDARY = "#6B7280";

interface Paycheque {
  total_pay?: number | string;
  gross_pay?: number | string;
  net_pay?: number | string;
  currency?: string;
  pay_date?: string;
  cheque_number?: string;
  first_name?: string;
  last_name?: string;
  employee_first_name?: string;
  employee_last_name?: string;
  employee_name?: string;
  [key: string]: unknown;
}

interface VoidPaychequeModalProps {
  open: boolean;
  onClose: () => void;
  paycheque: Paycheque | null;
  onConfirm: (reason: string) => Promise<void>;
}

export default function VoidPaychequeModal({
  open,
  onClose,
  paycheque,
  onConfirm,
}: VoidPaychequeModalProps): React.ReactElement | null {
  const [reason, setReason] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setReason(""); setError(null); setSubmitting(false); }
  }, [open]);

  if (!open || !paycheque) return null;

  const name = employeeNameFromPaycheque(paycheque as any);
  const grossLabel = formatCurrency(paycheque.total_pay || paycheque.gross_pay, paycheque.currency);
  const netLabel = formatCurrency(paycheque.net_pay, paycheque.currency);
  const dateLabel = formatDate(paycheque.pay_date);
  const chequeLabel = paycheque.cheque_number ? "Cheque #" + paycheque.cheque_number : null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason);
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not void paycheque";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "inherit" }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", width: 480, maxWidth: "100%", borderRadius: 10 }}>
        <div style={{ padding: "18px 22px 8px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#FEF3C7", color: "#92400E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <RotateCcw size={19} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ fontSize: 16, fontWeight: 500, color: TEXT_PRIMARY, margin: 0 }}>Void this paycheque?</h2>
              <div style={{ fontSize: 12, color: TEXT_SECONDARY, marginTop: 3 }}>The paycheque stays in your records marked VOIDED. This action cannot be undone.</div>
            </div>
          </div>

          <div style={{ background: "#F9FAFB", border: "0.5px solid " + BORDER, borderRadius: 8, padding: "11px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: TEXT_SECONDARY, marginBottom: 4 }}>Paycheque</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY }}>{name}</div>
            <div style={{ fontSize: 12, color: TEXT_SECONDARY, marginTop: 2 }}>
              {dateLabel}
              {grossLabel && (" \u00b7 " + grossLabel + " gross")}
              {netLabel && (" \u00b7 " + netLabel + " net")}
              {chequeLabel && (" \u00b7 " + chequeLabel)}
            </div>
          </div>

          <div style={{ background: "#FEF3C7", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#92400E", fontWeight: 500, marginBottom: 4 }}>Voiding will:</div>
            <div style={{ fontSize: 11, color: "#92400E", lineHeight: 1.6 }}>
              • Reverse YTD balances for CPP, EI, and income tax<br />
              • Keep the paycheque visible with a VOIDED mark for audit<br />
              • Leave you responsible for collecting payment from the employee (or paying them, if the cheque already cleared)
            </div>
          </div>

          <div>
            <label style={{ fontSize: 11, color: TEXT_SECONDARY, display: "block", marginBottom: 4 }}>Reason (required, recorded in audit trail)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Type a reason to enable Void (e.g. Cheque issued in error)"
              rows={2}
              style={{ width: "100%", padding: "7px 9px", borderRadius: 5, border: "0.5px solid " + BORDER, fontSize: 12, fontFamily: "inherit", color: TEXT_PRIMARY, boxSizing: "border-box", resize: "vertical" }}
            />
          </div>

          {error && (
            <div style={{ marginTop: 10, padding: "8px 10px", background: "#FEE2E2", color: "#991B1B", borderRadius: 6, fontSize: 12 }}>{error}</div>
          )}
        </div>

        <div style={{ padding: "12px 22px 16px", borderTop: "0.5px solid #F3F4F6", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} disabled={submitting} style={{ fontSize: 12, padding: "8px 16px", borderRadius: 5, background: "white", color: TEXT_PRIMARY, border: "0.5px solid " + BORDER, cursor: submitting ? "wait" : "pointer", fontWeight: 500, fontFamily: "inherit" }}>Cancel</button>
          <button onClick={handleConfirm} disabled={submitting || !reason.trim()} style={{ fontSize: 12, padding: "8px 16px", borderRadius: 5, background: (submitting || !reason.trim()) ? "#D6D3D1" : "#B45309", color: "white", border: "none", cursor: (submitting || !reason.trim()) ? "not-allowed" : "pointer", fontWeight: 500, fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <RotateCcw size={13} />
            {submitting ? "Voiding..." : "Void paycheque"}
          </button>
        </div>
      </div>
    </div>
  );
}
